import getMatchingVariant from '../utils/variants/getMatchingVariant';
import updatePrice from '../utils/variants/updatePrice';
import updateSelectedVariant from '../utils/variants/updateSelectedVariants';
import updateFormControls from '../utils/variants/updateFormControls';
import { registerVatRefresh } from '../utils/vatDisplay';
import updateURL from '../utils/variants/updateURL';
import preselectVariantFromURL from '../utils/variants/preselectVariantFromURL';
import updateStockIndicator from '../utils/variants/updateStockIndicator';

const ATTR_ACTIVE_VARIANT = 'data-active-variant';
const ATTR_ADDITIONAL_PRODUCT = 'data-additional-product-id';
const ATTR_ADDITIONAL_PLAN = 'data-additional-selling-plan-id';

const escapeSelector = str => {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(str);
  }
  return String(str).replace(/["\\]/g, '\\$&');
};

const GRAMS_IN_KG = 1000;
const KG_DECIMAL_PLACES = 2;

const formatVariantWeight = (weight, unit = 'g') => {
  const numericWeight = Number(weight);
  if (!Number.isFinite(numericWeight) || numericWeight <= 0) return '';
  const normalizedUnit = String(unit || 'g').toLowerCase();

  if (normalizedUnit === 'kg') {
    const kgWeight = numericWeight / GRAMS_IN_KG;
    return `${kgWeight % 1 === 0 ? kgWeight.toFixed(0) : kgWeight.toFixed(KG_DECIMAL_PLACES)} kg`;
  }

  if (normalizedUnit === 'lb' || normalizedUnit === 'oz') {
    return `${numericWeight} ${normalizedUnit}`;
  }

  return `${numericWeight} g`;
};

const updateAdditionalInfoTable = (variant) => {
  const additionalInfoRoot = document.querySelector('[data-additional-information]');
  if (!additionalInfoRoot || !variant) return;

  const skuRow = additionalInfoRoot.querySelector('[data-additional-info-sku-row]');
  const skuValue = additionalInfoRoot.querySelector('[data-additional-info-sku-value]');
  const weightRow = additionalInfoRoot.querySelector('[data-additional-info-weight-row]');
  const weightValue = additionalInfoRoot.querySelector('[data-additional-info-weight-value]');

  const sku = String(variant.sku || '').trim();
  if (skuRow && skuValue) {
    skuValue.textContent = sku;
    skuRow.classList.toggle('hidden', !sku);
  }

  const formattedWeight = formatVariantWeight(variant.weight, variant.weight_unit);
  if (weightRow && weightValue) {
    weightValue.textContent = formattedWeight;
    weightRow.classList.toggle('hidden', !formattedWeight);
  }
};

const syncGalleryToVariantMedia = (variant) => {
  const variantMediaId = variant?.featured_media?.id || variant?.featured_image?.id;
  if (!variantMediaId) return;

  const syncSwiperToMedia = (swiperEl) => {
    if (!swiperEl) return;
    const swiper = swiperEl.swiper;
    if (!swiper) return;

    const targetSlide = swiperEl.querySelector(`[data-product-media-id="${variantMediaId}"]`);
    if (!targetSlide) return;

    const targetIndex = Number(targetSlide.dataset.mediaIndex);
    if (!Number.isFinite(targetIndex)) return;

    if (typeof swiper.slideToLoop === 'function') {
      swiper.slideToLoop(targetIndex);
      return;
    }
    swiper.slideTo(targetIndex);
  };

  syncSwiperToMedia(document.querySelector('[data-product-gallery-main]'));
  syncSwiperToMedia(document.querySelector('[data-product-gallery-thumbs]'));
  syncSwiperToMedia(document.querySelector('[data-product-gallery-lightbox-swiper]'));
};

const initPdpPrice = ({ priceContainer, priceEl, compareAtPriceEl, badgeEl, saleRowEl }) => {
  if (!priceContainer || !priceEl) return;

  const applyPdpPrice = (salePrice, compareAt) => {
    updatePrice({
      priceEl,
      compareAtPriceEl,
      badgeEl,
      saleRowEl,
      price: salePrice,
      compareAtPrice: compareAt,
      isPdp: true,
    });
  };

  const salePrice = Number(priceContainer.dataset.variantPrice) || 0;
  const compareAt = Number(priceContainer.dataset.variantCompareAt) || 0;
  applyPdpPrice(salePrice, compareAt);

  registerVatRefresh(() => {
    applyPdpPrice(salePrice, compareAt);
  });
};

const initProductVariants = (container = document.querySelector('[data-product-main]')) => {
  if (!container) return;
  const variantEls = container.querySelectorAll('[data-variant-input]');
  const sellingPlanEls = container.querySelectorAll('[data-selling-plan-radio]');

  const priceContainer = document.querySelector('[data-product-price-pdp]');
  const priceEl = priceContainer?.querySelector('[data-product-price]');
  const compareAtPriceEl = priceContainer?.querySelector('[data-product-compare-at-price]');
  const badgeEl = priceContainer?.querySelector('[data-product-sale-badge]');
  const saleRowEl = priceContainer?.querySelector('[data-product-sale-row]');

  if (!variantEls.length && !sellingPlanEls.length) {
    initPdpPrice({ priceContainer, priceEl, compareAtPriceEl, badgeEl, saleRowEl });
    return;
  }

  const form = container.querySelector('[data-product-form]');
  if (!form) return;

  form.querySelectorAll('[data-variant-option-info-btn]').forEach(btn => {
    if (btn.dataset.variantInfoBound === '1') return;
    btn.dataset.variantInfoBound = '1';
    btn.addEventListener('click', e => e.stopPropagation());
  });

  const variantsDataEl = form.querySelector('[data-product-variants-json]');
  const optionNamesEl = form.querySelector('[data-product-options-json]');
  const variantInventoryEl = form.querySelector('[data-product-variants-inventory-policy-json]');
  const variantIdInput = form.querySelector('[data-atc-id-input]');
  const sellingPlanGroupContainerEl = form.querySelector('[data-selling-plan-groups]');
  const sellingPlanIdInput = sellingPlanGroupContainerEl?.querySelector('[data-selling-plan-input]');
  const sellingPlanInfoEls = sellingPlanGroupContainerEl?.querySelectorAll('[data-selling-plan-info]');
  const quantitySelector = form.querySelector('[data-quantity-selector]');
  const quantityInput = quantitySelector?.querySelector('[data-quantity-input]');
  const atcButton = form.querySelector('button[type="submit"]');
  const atcButtonPrice = atcButton?.querySelector('[data-atc-price]');
  const atcButtonMsgShort = atcButton?.querySelector('[data-atc-price-msg-short]');
  const atcButtonMsgFull = atcButton?.querySelector('[data-atc-price-msg-full]');
  const optionValueElement = form.querySelector('[data-option-value]');
  const stockIndicator = container.querySelector('[data-stock-indicator]');

  let variantsData = [];
  let optionNames = [];
  let variantInventory = [];

  try {
    variantsData = JSON.parse(variantsDataEl?.textContent || '[]');
    optionNames = JSON.parse(optionNamesEl?.textContent || '[]');
    variantInventory = JSON.parse(variantInventoryEl?.textContent || '[]');
  } catch (e) {
    console.error('Invalid variant/options JSON', e);
    return;
  }

  const selectedOptions = {};

  const clearAtcVariantMarkers = () => {
    variantEls.forEach(el => {
      el.removeAttribute(ATTR_ACTIVE_VARIANT);
      if (el.tagName === 'SELECT' || el.type === 'radio') {
        el.removeAttribute(ATTR_ADDITIONAL_PRODUCT);
        el.removeAttribute(ATTR_ADDITIONAL_PLAN);
      }
    });
  };

  const applyAtcVariantMarkerForSelect = select => {
    clearAtcVariantMarkers();
    select.setAttribute(ATTR_ACTIVE_VARIANT, '');
    const opt = select.selectedOptions[0];
    if (opt?.hasAttribute(ATTR_ADDITIONAL_PRODUCT)) {
      select.setAttribute(ATTR_ADDITIONAL_PRODUCT, opt.getAttribute(ATTR_ADDITIONAL_PRODUCT));
    }
    if (opt?.hasAttribute(ATTR_ADDITIONAL_PLAN)) {
      select.setAttribute(ATTR_ADDITIONAL_PLAN, opt.getAttribute(ATTR_ADDITIONAL_PLAN));
    }
  };

  const applyAtcVariantMarkerForRadio = radio => {
    clearAtcVariantMarkers();
    radio.setAttribute(ATTR_ACTIVE_VARIANT, '');
    if (radio.hasAttribute(ATTR_ADDITIONAL_PRODUCT)) {
      radio.setAttribute(ATTR_ADDITIONAL_PRODUCT, radio.getAttribute(ATTR_ADDITIONAL_PRODUCT));
    }
    if (radio.hasAttribute(ATTR_ADDITIONAL_PLAN)) {
      radio.setAttribute(ATTR_ADDITIONAL_PLAN, radio.getAttribute(ATTR_ADDITIONAL_PLAN));
    }
  };

  const readSelectedOptionsFromDom = () => {
    optionNames.forEach(name => {
      const en = escapeSelector(name);
      const sel = form.querySelector(`select[name="${en}"]`);
      const radio = form.querySelector(`input[type="radio"][name="${en}"]:checked`);
      if (sel) {
        selectedOptions[name] = sel.value;
      } else if (radio) {
        selectedOptions[name] = radio.value;
      }
    });
  };

  const resolveVariantPrices = (variant) => {
    const salePrice = Number(variant?.price) || 0;
    const compareAt =
      Number(variant?.compare_at_price) ||
      Number(priceContainer?.dataset.variantCompareAt) ||
      0;
    return { salePrice, compareAt };
  };

  const updateVariantDetails = (variantStock) => {
    const variant = getMatchingVariant(selectedOptions, variantsData, optionNames);
    if (!variant) return;

    const { salePrice, compareAt } = resolveVariantPrices(variant);

    variantIdInput && (variantIdInput.value = variant.id);
    updatePrice({
      priceEl,
      compareAtPriceEl,
      badgeEl,
      saleRowEl,
      price: salePrice,
      compareAtPrice: compareAt,
      isPdp: true,
    });

    updateSelectedVariant({
      variant,
      variantInventory,
      elements: {
        atcButtonMsgShort,
        atcButtonMsgFull,
        optionValueElement,
        stockIndicator
      },
      strings: window.theme.strings
    });

    updateURL({ variantID: variant.id, sellingPlanIdInput });
    syncGalleryToVariantMedia(variant);
    updateAdditionalInfoTable(variant);

    updateFormControls({
      variant,
      qty: +(quantityInput?.value || 1),
      variantsData,
      optionNames,
      selectedOptions,
      atcButton,
      atcButtonPrice,
      quantitySelector
    });

    const inv = variant.inventory_quantity;
    const invStr =
      inv != null && inv !== '' && Number.isFinite(Number(inv)) ? String(inv) : '';
    if (stockIndicator) {
      updateStockIndicator(stockIndicator, invStr || null, variant.available !== false);
    }

    if (quantitySelector) {
      quantitySelector.dataset.maxQuantity = invStr || variantStock || '';
    }
  };

  const updateSelectedOption = (name, value, variantStock) => {
    selectedOptions[name] = value;
    updateVariantDetails(variantStock);
  };

  if (variantEls.length) {
    variantEls.forEach(variantEl => {
      variantEl.addEventListener('change', ({ target }) => {
        if (target.tagName === 'SELECT') {
          applyAtcVariantMarkerForSelect(target);
        } else if (target.type === 'radio') {
          applyAtcVariantMarkerForRadio(target);
        }

        let variantStock = '';
        if (target.tagName === 'SELECT') {
          const opt = target.selectedOptions[0];
          variantStock = opt?.getAttribute('data-max-quantity') ?? '';
        } else {
          variantStock = target.getAttribute('data-max-quantity') ?? '';
        }

        if (stockIndicator) {
          updateStockIndicator(stockIndicator, variantStock);
        }
        const { name, value } = target;
        updateSelectedOption(name, value, variantStock);
      });
    });
  }

  readSelectedOptionsFromDom();

  preselectVariantFromURL({
    variantsData,
    optionNames,
    form,
    selectedOptions,
    sellingPlanIdInput,
    updateVariantDetails
  });

  readSelectedOptionsFromDom();

  const checkedRadios = form.querySelectorAll('input[type="radio"][data-variant-input]:checked');
  if (checkedRadios.length) {
    applyAtcVariantMarkerForRadio(checkedRadios[checkedRadios.length - 1]);
  } else {
    const selects = form.querySelectorAll('select[data-variant-input]');
    if (selects.length) {
      applyAtcVariantMarkerForSelect(selects[selects.length - 1]);
    }
  }

  const v = getMatchingVariant(selectedOptions, variantsData, optionNames);
  if (v && !new URL(window.location.href).searchParams.get('variant')) {
    const variantStock = String(v.inventory_quantity ?? '');
    updateVariantDetails(variantStock);
  }

  if (priceEl) {
    registerVatRefresh(() => {
      const variant = getMatchingVariant(selectedOptions, variantsData, optionNames);
      let salePrice = Number(priceContainer?.dataset.variantPrice) || 0;
      let compareAt = Number(priceContainer?.dataset.variantCompareAt) || 0;
      if (variant) {
        const resolved = resolveVariantPrices(variant);
        salePrice = resolved.salePrice;
        compareAt = resolved.compareAt;
      }
      updatePrice({
        priceEl,
        compareAtPriceEl,
        badgeEl,
        saleRowEl,
        price: salePrice,
        compareAtPrice: compareAt,
        isPdp: true,
      });
      updateFormControls({
        variant,
        qty: +(quantityInput?.value || 1),
        atcButton,
        atcButtonPrice,
        quantitySelector
      });
      if (stockIndicator && variant) {
        const inv = variant.inventory_quantity;
        const invStr =
          inv != null && inv !== '' && Number.isFinite(Number(inv)) ? String(inv) : '';
        updateStockIndicator(stockIndicator, invStr || null, variant.available !== false);
      }
    });
  }
};

export default initProductVariants;
